/**
 * Triniticare — Notification Engine
 *
 * Resolves backend errors into professional, healthcare-friendly
 * toast notifications. Feature-flag gated.
 *
 * Usage:
 *   import { notify } from '@/lib/notifications/engine';
 *
 *   // In catch blocks:
 *   notify.error(e, 'SERVICE_ITEM_CREATE_FAILED');
 *
 *   // Direct:
 *   notify.success('Timesheet approved successfully');
 *   notify.warn('FUNDING_INACTIVE');
 */

import { toast } from 'sonner';
import { MESSAGE_REGISTRY, BACKEND_MESSAGE_MAP } from './messageRegistry';

// Feature flag check (reads from localStorage, set by feature flag API)
function isEngineEnabled() {
  try {
    const flags = JSON.parse(localStorage.getItem('gc360_feature_flags') || '{}');
    return !!flags.common_notification_engine_enabled;
  } catch {
    return false;
  }
}

/**
 * Extract a human-readable message from an axios error response.
 */
function extractBackendMessage(error) {
  if (!error?.response?.data) return null;
  const d = error.response.data;
  // Format: { error: { code, message } }
  if (d.error?.message) return d.error.message;
  // Format: { detail: string } or { detail: { message } }
  if (typeof d.detail === 'string') return d.detail;
  if (d.detail?.message) return d.detail.message;
  // Format: { message: string }
  if (d.message) return d.message;
  return null;
}

/**
 * Extract error code from backend response.
 */
function extractBackendCode(error) {
  if (!error?.response?.data) return null;
  const d = error.response.data;
  if (d.error?.code) return d.error.code;
  if (d.detail?.code) return d.detail.code;   // UA-6-F24: FastAPI HTTPException(detail={code,…}) shape
  if (d.code) return d.code;
  return null;
}

/**
 * Resolve a registry entry from an error or code string.
 */
function resolve(errorOrCode, fallbackCode) {
  // 1. Try direct registry lookup by code
  if (typeof errorOrCode === 'string' && MESSAGE_REGISTRY[errorOrCode]) {
    return MESSAGE_REGISTRY[errorOrCode];
  }

  // 2. Try backend error code
  if (errorOrCode?.response) {
    const backendCode = extractBackendCode(errorOrCode);
    if (backendCode && MESSAGE_REGISTRY[backendCode]) {
      return MESSAGE_REGISTRY[backendCode];
    }

    // 3. Try mapping backend message to registry code
    const backendMsg = extractBackendMessage(errorOrCode);
    if (backendMsg) {
      const mappedCode = BACKEND_MESSAGE_MAP[backendMsg];
      if (mappedCode && MESSAGE_REGISTRY[mappedCode]) {
        return MESSAGE_REGISTRY[mappedCode];
      }

      // 4. Check for permission errors
      if (backendMsg.includes('Missing permission') || errorOrCode.response.status === 403) {
        return {
          ...MESSAGE_REGISTRY.PERMISSION_DENIED,
          message: backendMsg,
        };
      }
    }

    // 5. Network/server errors by status
    if (errorOrCode.response.status >= 500) {
      return MESSAGE_REGISTRY.SERVER_UNAVAILABLE;
    }
  }

  // 6. Network errors (no response)
  if (errorOrCode?.code === 'ERR_NETWORK' || !errorOrCode?.response) {
    if (typeof errorOrCode !== 'string') {
      return MESSAGE_REGISTRY.NETWORK_ERROR;
    }
  }

  // 7. Try fallback code
  if (fallbackCode && MESSAGE_REGISTRY[fallbackCode]) {
    return MESSAGE_REGISTRY[fallbackCode];
  }

  // 8. Final fallback
  return MESSAGE_REGISTRY.UNKNOWN;
}

/**
 * Show a structured toast notification.
 */
function showStructured(entry, backendMsg) {
  const description = [
    entry.message,
    entry.action ? `→ ${entry.action}` : null,
  ].filter(Boolean).join('\n');

  const opts = { description, duration: 5000 };

  switch (entry.severity) {
    case 'success':
      toast.success(entry.title, opts);
      break;
    case 'warning':
      toast.warning(entry.title, opts);
      break;
    case 'info':
      toast.info(entry.title, opts);
      break;
    case 'error':
    default:
      toast.error(entry.title, opts);
      break;
  }
}

/**
 * Legacy fallback — shows the old-style toast when feature flag is OFF.
 */
function showLegacy(error, fallbackMessage) {
  const msg = extractBackendMessage(error) || fallbackMessage || 'Failed';
  toast.error(msg);
}

/**
 * Notification engine public API.
 */
export const notify = {
  /**
   * Show an error notification from a caught error.
   * @param {Error|object} error - Axios error or Error object
   * @param {string} fallbackCode - Registry code to use if error can't be resolved
   * @param {string} legacyFallback - Legacy fallback message (when flag is OFF)
   */
  error(error, fallbackCode, legacyFallback) {
    if (!isEngineEnabled()) {
      showLegacy(error, legacyFallback || 'Failed');
      return;
    }
    const entry = resolve(error, fallbackCode);
    // Enrich with backend message if available
    const backendMsg = extractBackendMessage(error);
    const backendCode = extractBackendCode(error);
    // UA-6-F24/F21: a generic fallback entry (e.g. SAVE_FAILED "Unable to save") must never hide a structured 4xx reason —
    // show the backend message + code instead of the generic text/action.
    const generic = entry === MESSAGE_REGISTRY.UNKNOWN || (fallbackCode && entry === MESSAGE_REGISTRY[fallbackCode]);
    if (backendMsg && generic) {
      showStructured({ ...entry, message: backendCode ? `${backendMsg} [${backendCode}]` : backendMsg, action: null }, backendMsg);
    } else {
      showStructured(entry, backendMsg);
    }
  },

  /**
   * Show a warning notification.
   * @param {string} codeOrMessage - Registry code or plain message
   */
  warn(codeOrMessage) {
    if (!isEngineEnabled()) {
      const entry = MESSAGE_REGISTRY[codeOrMessage];
      toast.warning(entry?.title || codeOrMessage);
      return;
    }
    const entry = resolve(codeOrMessage);
    showStructured(entry);
  },

  /**
   * Show a success notification.
   * @param {string} message - Success message
   */
  success(message) {
    toast.success(message);
  },

  /**
   * Show an info notification.
   * @param {string} message - Info message
   */
  info(message) {
    toast.info(message);
  },
};

export default notify;
